import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { LocationCarrierConfiguration } from '../../../shared/location-carrier-configuration.model';
import { CarrierDisplay } from '../../../../user-dashboard/shared/carrier-display.model';
import { CarrierService } from '../../../../user-dashboard/shared/carrier.service';
import { SearchObjectResult } from '../../../../shared/search-object-result.model';
import { DataTableColumn } from '../../../../shared/data-table/data-table.component';
import { Observable, of } from 'rxjs';
import { LocalLocationService } from '../../../shared/local-location.service';
import { filter, map, tap } from 'rxjs/operators';
import { ModalService } from '../../../../shared/services/modal.service';
import { TranslateService } from '@ngx-translate/core';
import {
  LocationCarrierCmaLiteConfigurationUpdateRequest
} from '../../../shared/location-carrier-cma-lite-configuration-update-request.model';
import { DataFiltersComponent } from '../../../../shared/data-filters/data-filters.component';
import { ModalComponent } from '../../../../core/modal/modal.component';
import { SortDirection } from '../../../../shared/sort-direction';

@Component({
  selector: 'app-location-carrier-configuration-edit',
  templateUrl: './location-carrier-configuration-edit.component.html',
  styleUrls: ['./location-carrier-configuration-edit.component.scss']
})
export class LocationCarrierConfigurationEditComponent implements OnInit {
  private readonly MIN_SEARCH_CHARS = 3;

  @Input()
  locationContainerId: string;

  @Output() closeRequested: EventEmitter<void> = new EventEmitter();

  @ViewChild('configDataFilters', {static: true}) private configFiltersComponent: DataFiltersComponent;
  @ViewChild('carriersDataFilters', {static: true}) private carriersDataFiltersComponent: DataFiltersComponent;

  @ViewChild('dedicatedModalComponent', {static: true}) private dedicatedModalComponent: ModalComponent;

  carriers: SearchObjectResult<CarrierDisplay>;

  carrierFilters: { name?: string, vendorNumber?: string } = {};
  configFilters: { name?: string, vendorNumber?: string } = {};

  sortColumn: DataTableColumn;
  carrierColumns: DataTableColumn[];

  configuredCarriersFromServer: CarrierDisplay[] = [];

  configuredCarriersToDisplay: CarrierDisplay[] = [];

  configuredCarriersToDisplayFiltered: CarrierDisplay[];

  addedCarriers: CarrierDisplay[] = [];

  removedCarriers: CarrierDisplay[] = [];

  loadCarriersPage: (...config) => Observable<SearchObjectResult<CarrierDisplay>>;

  @ViewChild('carrierName', {static: true})
  carrierName: TemplateRef<any>;

  @ViewChild('vendorNumber', {static: true})
  vendorNumber: TemplateRef<any>;

  @ViewChild('enableCmaLiteColumn', {static: true})
  enableCmaLiteColumn: TemplateRef<any>;

  SortDirection = SortDirection;

  constructor(private carrierService: CarrierService,
              private modalService: ModalService,
              private translate: TranslateService,
              private locationService: LocalLocationService) {

    this.loadCarriersPage = (...config): Observable<SearchObjectResult<CarrierDisplay>> => {
      const mConfig = {
        page: config[0] - 1,
        pageSize: config[1],
        sortColumn: config[2],
        direction: config[3],
      };
      return this.carrierService.searchForDisplayCarriers(
        mConfig.page,
        mConfig.pageSize,
        mConfig.sortColumn.name,
        mConfig.direction,
        {name: this.carrierFilters.name, sap_vendor: this.carrierFilters.vendorNumber});
    }
  }

  ngOnInit(): void {

    this.refreshConfiguredCarriers();

    if (this.configFiltersComponent) {
      this.configFiltersComponent.getResetObservable().subscribe(() => this.resetConfigFilters());
      this.configFiltersComponent.getApplyObservable().subscribe(() => this.updateFilteredConfigs());
    }

    if (this.carriersDataFiltersComponent) {
      this.carriersDataFiltersComponent.getResetObservable().subscribe(() => this.resetCarrierFilters());
    }

    this.carrierColumns = [
      {
        name: 'name',
        displayed: true,
        translationKey: 'location-carrier-configuration.carrier-list.name',
        template: this.carrierName,
        sortable: true,
        alignCenter: true,
      },
      {
        name: 'sap_vendor',
        displayed: true,
        translationKey: 'location-carrier-configuration.carrier-list.vendor-number',
        template: this.vendorNumber,
        sortable: false,
        alignCenter: true,
      },
      {
        name: 'enableCmaLiteColumn',
        displayed: true,
        translationKey: 'location-carrier-configuration.carrier-list.add',
        template: this.enableCmaLiteColumn,
        sortable: false,
        alignCenter: true,
      }
    ];
    this.sortColumn = this.carrierColumns.find((col: DataTableColumn) => col.name === 'name');

  }

  private resetCarrierFilters() {
    this.carrierFilters = {};
  }

  private resetConfigFilters() {
    this.configFilters = {};
    this.updateFilteredConfigs();
  }

  refreshConfiguredCarriers() {
    this.locationService.getCarrierConfigurations(this.locationContainerId).pipe(
      map(carrierConfigs => carrierConfigs.filter(config => config.cmaLiteEnabled)),
      map(results => results.map(result => CarrierDisplay.fromLocationCarrierConfig(result))),
      tap(results => this.configuredCarriersFromServer = results)
    ).subscribe(
      () => this.refreshConfiguredCarriersToDisplay()
    )
  }

  addCarrier(carrierToAdd: CarrierDisplay): void {

    const indexInCarriersToRemove = this.removedCarriers.findIndex(carrier => carrier.id === carrierToAdd.id);
    if (indexInCarriersToRemove !== -1) {
      this.removedCarriers.splice(indexInCarriersToRemove, 1);
    } else {
      this.addedCarriers.push(carrierToAdd);
    }

    this.refreshConfiguredCarriersToDisplay();

  }

  removeCarrier(carrierToRemove: CarrierDisplay): void {
    const indexInCarriersToAdd = this.addedCarriers.findIndex(carrier => carrier.id === carrierToRemove.id);
    if (indexInCarriersToAdd !== -1) {
      this.addedCarriers.splice(indexInCarriersToAdd, 1);
    } else {
      this.removedCarriers.push(carrierToRemove);
    }

    this.refreshConfiguredCarriersToDisplay()

  }


  private refreshConfiguredCarriersToDisplay() {
    this.configuredCarriersToDisplay = this.configuredCarriersFromServer.concat(this.addedCarriers)
      .filter(carrier =>
        this.removedCarriers.findIndex(removedCarrier => removedCarrier.id === carrier.id) === -1
      );

    this.updateFilteredConfigs();
  }

  itemMissingInfo(item: CarrierDisplay) {
    return !item.vendorNumber;
  }

  itemAdded(item: CarrierDisplay) {
    return this.configuredCarriersToDisplay.findIndex(carrier => carrier.id === item.id) > -1;
  }

  confirmUpdateLocationCarrierConfig(content: TemplateRef<any>) {

    this.dedicatedModalComponent.open({
      size: 'confirm',
      zIndex: 2010,
      contentClass: 'confirmList',
      title: this.translate.instant('location-carrier-configuration.modals.confirm.title'),
      content,
      data: {
        addedCarrierNames: this.addedCarriers.map(c => c.name),
        removedCarrierNames: this.removedCarriers.map(c => c.name)
      },
      closeButton: {
        text: this.translate.instant('location-carrier-configuration.modals.confirm.cancel')
      },
      validateButton: {
        text: this.translate.instant('location-carrier-configuration.modals.confirm.save'),
        action: () => this.updateLocationCarrierConfig()
      }
    })
  }


  private updateLocationCarrierConfig() {
    const updateRequest = new LocationCarrierCmaLiteConfigurationUpdateRequest();

    updateRequest.enabledCarriers = this.configuredCarriersToDisplay.map(carrier => carrier.id);

    this.locationService.updateCarrierCmaLiteConfigurations(this.locationContainerId, updateRequest).subscribe(
      () => this.close()
    )
  }

  confirmClose(content: TemplateRef<any>) {

    if (this.removedCarriers.length || this.addedCarriers.length) {
      this.dedicatedModalComponent.open({
        size: 'confirm',
        zIndex: 2010,
        title: this.translate.instant('location-carrier-configuration.modals.dirty-check.title'),
        content,
        data: {
          addedCarrierNames: this.addedCarriers.map(c => c.name),
          removedCarrierNames: this.removedCarriers.map(c => c.name)
        },
        closeButton: {
          text: this.translate.instant('location-carrier-configuration.modals.dirty-check.no'),
        },
        validateButton: {
          text: this.translate.instant('location-carrier-configuration.modals.dirty-check.yes'),
          action: () => this.close()
        }
      });
    } else {
      this.close();
    }
  }

  private close() {
    this.closeRequested.emit();
  }

  private updateFilteredConfigs() {
    this.configuredCarriersToDisplayFiltered = this.configuredCarriersToDisplay.filter(
      config =>
        (!this.configFilters.name || config.name.toLowerCase().indexOf(this.configFilters.name.toLowerCase()) > -1) &&
        (
          !this.configFilters.vendorNumber || (
            config.vendorNumber && config.vendorNumber.toLowerCase().indexOf(this.configFilters.vendorNumber.toLowerCase()) > -1
          )
        )
    )
  }

  carrierSearchDisabled() {
    return (this.carrierFilters.name && this.carrierFilters.name.length < this.MIN_SEARCH_CHARS) ||
            (this.carrierFilters.vendorNumber && this.carrierFilters.vendorNumber.length < this.MIN_SEARCH_CHARS);
  }

  configSearchDisabled() {
    return (this.configFilters.name && this.configFilters.name.length < this.MIN_SEARCH_CHARS) ||
            (this.configFilters.vendorNumber && this.configFilters.vendorNumber.length < this.MIN_SEARCH_CHARS);
  }

  getLabel(key: string) {
    return this.translate.instant(key);
  }
}
